import { useFaireDirectFirstOrderIncentive } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { SignUpTitle } from "@faire/retailer-visitor-shared/views/SignUp/_components/SignUpTitle";
import { useRetailerSubUser } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/retailerSubUserHooks";
import { ProductImages } from "@faire/retailer-visitor-shared/views/SignUp/personal/__internal__/faire-direct/ProductImages";
import { SSOAvatar } from "@faire/retailer-visitor-shared/views/SignUp/personal/__internal__/SSOAvatar";
import { SubUserWelcome } from "@faire/retailer-visitor-shared/views/SignUp/personal/__internal__/sub-user/SubUserWelcome";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { MediumFormatting } from "@faire/web--source/common/localization/formatting";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React from "react";
import styled from "styled-components";

export const PersonalInfoTitle: React.FC<{
  isSubUser: boolean;
  isSSO: boolean;
  isFaireDirectRequiredEmailAddressSignUpFlow: boolean;
  loadTimingRef?: React.RefCallback<HTMLElement>;
}> = ({
  isSubUser,
  isSSO,
  isFaireDirectRequiredEmailAddressSignUpFlow,
  loadTimingRef,
}) => {
  if (isSubUser) {
    return <SubUserTitle />;
  }

  if (isSSO) {
    return <SSOTitle />;
  }

  if (isFaireDirectRequiredEmailAddressSignUpFlow) {
    return (
      <FaireDirectRequiredEmailAddressSignUpTitle
        loadTimingRef={loadTimingRef}
      />
    );
  }

  return <GenericTitle />;
};

const GenericTitle: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  return (
    <SignUpTitle
      title={strictLocalize({
        defaultMessage: "Welcome! Let’s get started",
        description: {
          img: "https://cdn.faire.com/fastly/53bd69dd02093ccaf81c4c3638dfe5ce20321981b6710c41e55ba84d5caf7f20.png",
          text: "Welcome message for the sign up flow",
        },
      })}
    />
  );
};

const SubUserTitle: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { invitation } = useRetailerSubUser();
  return (
    <>
      <SignUpTitle
        title={strictLocalize({
          defaultMessage: "Welcome! Join your team",
          description: {
            img: "https://cdn.faire.com/fastly/4707d8e4ec364ee9a3a0f465f6c32adaa433a7cac38ce1e1df698dcce18ead95.png",
            text: "Welcome message for the sign up flow",
          },
        })}
      />
      {invitation ? (
        <>
          <Spacer height="1x" />
          {/* TODO(Tiba): completely remove the title from SubUserWelcome once we fully rollout revamp version */}
          <SubUserWelcome subUserInvite={invitation} hideTitle />
        </>
      ) : null}
    </>
  );
};

const SSOTitle: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { signUpSuggestions } = useSSOStore();

  if (signUpSuggestions.first_name) {
    return (
      <>
        <SignUpTitle
          title={strictLocalize(
            {
              defaultMessage: "Welcome, {firstPersonName}! Let’s get started",
              description: {
                img: "https://cdn.faire.com/fastly/085633f972f8b597da043d0a8c104ad0f1675bd7574adbe36a9122825594ecfc.png",
                text: "Welcome message for the sign up flow",
              },
            },
            {
              firstPersonName: signUpSuggestions.first_name,
            }
          )}
        />
        <SSOAvatar />
      </>
    );
  }

  return (
    <>
      <GenericTitle />
      <SSOAvatar />
    </>
  );
};

const FaireDirectRequiredEmailAddressSignUpTitle: React.FC<{
  loadTimingRef?: React.RefCallback<HTMLElement>;
}> = ({ loadTimingRef }) => {
  const { strictLocalize } = useStrictLocalization();
  const {
    formattedPercentageBasedNumber,
    isFaireDirectCreditPercentageBased,
    formattedFixedBasedAmount,
    brandName,
    brandToken,
  } = useFaireDirectFirstOrderIncentive();

  return (
    <TitleContainer>
      <ProductImages brandToken={brandToken} loadTimingRef={loadTimingRef} />
      <SignUpTitle
        title={strictLocalize(
          {
            defaultMessage: "Try {brandCompanyName} in your store, risk-free",
            description: {
              img: "https://cdn.faire.com/fastly/085633f972f8b597da043d0a8c104ad0f1675bd7574adbe36a9122825594ecfc.png",
              text: "Welcome message for the sign up flow",
            },
          },
          {
            brandCompanyName: brandName ?? "",
          }
        )}
        subtitle={
          isFaireDirectCreditPercentageBased ? (
            <StrictLocalMsg
              defaultMessage="Sign up for Faire to order our wholesale products online. Join today and get <MediumFormatting>{promoPercentageNumber} off and 1 year of free shipping.</MediumFormatting>*"
              description={{
                img: "https://cdn.faire.com/fastly/5a0731a5fcd2ba31797683b8206e9a8a49d53e5aed36a3193a8c399d5704ff72.png",
                text: "subtitle letting retailer know about the faire direct incentive (percentage based)",
              }}
              values={{
                MediumFormatting,
                promoPercentageNumber: formattedPercentageBasedNumber,
              }}
            />
          ) : (
            <StrictLocalMsg
              defaultMessage="Sign up for Faire to order our wholesale products online. Join today and get <MediumFormatting>{promoAmountMoney} off and 1 year of free shipping.</MediumFormatting>*"
              description={{
                img: "https://cdn.faire.com/fastly/3b163db14b1053ad4fabc444c1c91c3ffa99c67301f2f138cd5935bb8fa6f86d.png",
                text: "subtitle letting retailer know about the faire direct incentive (amount based)",
              }}
              values={{
                MediumFormatting,
                promoAmountMoney: formattedFixedBasedAmount,
              }}
            />
          )
        }
      />
    </TitleContainer>
  );
};

const TitleContainer = styled.div`
  // we have to compensate for the padding on the parent container
  ${responsiveMediaQueries.mobile} {
    margin: ${getSpacing("-4x")} 0 0;
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    margin: ${getSpacing("-3x")} 0 0;
  }
`;
